import React, { useEffect, useState } from 'react';
import { useUserContext } from 'contexts/UserContext';
import StudentGradebook from 'models/StudentGradebook';
import studentService from 'services/studentService';
import StudentProfile from 'models/StudentProfile';
import { useClassroomContext } from 'contexts/ClassroomContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssignmentContext } from 'contexts/AssignmentContext';
import GradebookEntry from 'models/StudentGradebookEntry';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import { AppPage } from 'view/components/common/AppPage';
import userService from 'services/userService';
import useApiCall from 'contexts/ApiCall';
import AppSidebar from 'view/components/common/Sidebar';
import contentService from 'services/contentService';
import { SearchBar } from 'view/components/common/SearchBar';
import { faGraduationCap, faSchool } from '@fortawesome/free-solid-svg-icons';
import EvaluationSubmission from 'models/EvaluationSubmission';
import AssignmentDetails from 'view/components/student/classroom/AssignmentDetails';
import EvaluationPreviewPopup from 'view/components/student/assignment/evaluation/EvaluationPreviewPopup';
import HeaderRowButton from 'view/components/buttons/HeaderButton';
import Module from 'models/Module';
import AssignmentDisplay from 'view/components/student/classroom/AssignmentDisplay';

import 'view/style/student/classroom.css';

export const StudentClassPage: React.FC = () => {
  const { user } = useUserContext();
  const { classroom, setClassroom } = useClassroomContext();
  const { classroomId } = useParams();
  const { setAssignment } = useAssignmentContext();
  const [gradebook, setGradebook] = useState<StudentGradebook>();
  const [studentProfile, setStudentProfile] = useState<StudentProfile>();
  const [previewEntry, setPreviewEntry] = useState<StudentGradebookEntry>();
  const [previewModule, setPreviewModule] = useState<Module | null>();
  const [evaluationPreview, setEvaluationPreview] =
    useState<EvaluationSubmission>();
  const pageContent = contentService.getStudentClassroomPage();
  const makeApiCall = useApiCall();
  const navigate = useNavigate();

  // Single effect to load classroom data - using a stable key to prevent multiple calls
  useEffect(() => {
    // Only run if we have a user, classroomId, and don't already have the classroom
    if (!user || !classroomId || classroom) return;

    console.debug(`CLASSROOM: Loading classroom data for ID ${classroomId}`);

    // Use AbortController to handle cleanup
    const controller = new AbortController();

    makeApiCall(userService.getClassroomById, classroomId)
      .then((resp) => {
        if (controller.signal.aborted) return;

        console.debug(
          `CLASSROOM: Successfully loaded classroom ${resp.classroom.name}`,
        );
        setClassroom(resp.classroom);

        // If we got student profile data, set it
        if (resp.student) {
          console.debug(`CLASSROOM: Student profile loaded`);
          setStudentProfile(resp.student);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('CLASSROOM: Error loading classroom', error);
        navigate(-1);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [
    // Stable dependencies that won't change during component lifecycle
    classroomId,
    // We include user.getId() instead of user to prevent re-runs when other user properties change
    user?.id,
    // Include classroom to prevent fetching if we already have it
    classroom,
    navigate,
    setClassroom,
    makeApiCall,
  ]);

  // Only load student profile if we didn't get it from the first API call
  useEffect(() => {
    // Skip if:
    // 1. We already have student profile from the first call
    // 2. Missing user or classroom data
    // 3. We're already loading the profile in another effect
    if (studentProfile || !user || !classroom) return;

    console.debug(`CLASSROOM: Loading student profile separately`);

    // Use AbortController for cleanup
    const controller = new AbortController();

    makeApiCall(studentService.getStudentProfile, user, classroom)
      .then((respStudentProfile) => {
        if (controller.signal.aborted) return;

        console.debug(`CLASSROOM: Student profile loaded separately`);
        setStudentProfile(respStudentProfile);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('CLASSROOM: Error loading student profile', error);
        alert(error.message);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [
    // Only depend on IDs, not whole objects
    classroom?.id,
    user?.id,
    studentProfile,
    makeApiCall,
  ]);

  // Load gradebook once we have the student profile
  useEffect(() => {
    // Skip if:
    // 1. We already have the gradebook
    // 2. Missing required data
    if (gradebook || !studentProfile || !classroom) return;

    console.debug(
      `CLASSROOM: Loading gradebook for student ${studentProfile.id}`,
    );

    // Use AbortController for cleanup
    const controller = new AbortController();

    makeApiCall(studentService.getGradebook, studentProfile.id, classroom.id)
      .then((respGradebook) => {
        if (controller.signal.aborted) return;

        console.debug(
          `CLASSROOM: Gradebook loaded with ${respGradebook.entries.length} entries`,
        );
        setGradebook(respGradebook);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('CLASSROOM: Error loading gradebook', error);
        alert(error.message);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [
    // Only depend on IDs and stable values
    studentProfile?.id,
    classroom?.id,
    // Check if we already have the gradebook
    gradebook,
    makeApiCall,
  ]);

  const handleAssignmentSelect = (entry: GradebookEntry) => {
    setPreviewModule(null);
    if (previewEntry === entry) {
      setAssignment(entry);
      navigate(`assignment/${entry.assignment.id}/`);
    }
    setPreviewEntry(entry);
  };

  const handleModuleSelect = (module: Module) => {
    setAssignment(null);
    setPreviewEntry(undefined);
    if (previewModule === module) {
      navigate(`module/${module.id}/`);
    }
    setPreviewModule(module);
  };

  const handleEvaluationSelect = (sub: EvaluationSubmission) => {
    setEvaluationPreview(sub);
  };

  const handleExitClassroom = () => {
    setClassroom(null);
    if (user?.account_type.toLocaleLowerCase() === 'teacher') {
      navigate(`/teacher/classroom/${classroomId}/`);
    } else {
      navigate('/student/home/');
    }
  };

  const filterGradebook = (searchTerm: string) => {
    if (!gradebook) return;
    const filteredGradebook = StudentGradebook.fromStudentGradebook(gradebook);
    filteredGradebook.entries.forEach(
      (entry) =>
        (entry.visible = entry.assignment.title
          .toLowerCase()
          .includes(searchTerm.toLowerCase())),
    );
    setGradebook(filteredGradebook);
  };

  const switchToTeacherView = () => {
    if (!classroomId) return;
    navigate(`/teacher/classroom/${classroomId}`);
  };

  const handleViewPractice = () => navigate(`practice/${studentProfile?.id}`);

  return (
    <AppPage>
      <div className="app-page-content">
        <AppSidebar onExit={handleExitClassroom}>
          <>
            <SearchBar onSearch={filterGradebook} />
            <div className="sidebar-content-data">
              <p>{pageContent.sidebar.average.title}</p>
              <b>{gradebook ? gradebook.averages.grade : 0}%</b>
            </div>
            {previewEntry && <AssignmentDetails entry={previewEntry} />}
          </>
        </AppSidebar>
        <div className="app-main-content">
          <div className="row spaced">
            <h1>{classroom?.name}</h1>
            {user?.account_type === 'Teacher' ? (
              <HeaderRowButton
                label="Teacher view"
                icon={faGraduationCap}
                onClick={switchToTeacherView}
              />
            ) : (
              <HeaderRowButton
                label="Practice"
                icon={faSchool}
                onClick={handleViewPractice}
              />
            )}
          </div>
          {gradebook && (
            <AssignmentDisplay
              gradebook={gradebook}
              onAssignmentSelect={handleAssignmentSelect}
              onModuleSelect={handleModuleSelect}
              onEvaluationSelect={handleEvaluationSelect}
            />
          )}
        </div>
      </div>
      <EvaluationPreviewPopup
        evaluationPreview={evaluationPreview}
        isOpen={evaluationPreview !== undefined}
        onClose={() => setEvaluationPreview(undefined)}
      />
    </AppPage>
  );
};
